/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router-dom';
import { AppShell } from '@/app/layout/AppShell';

/* ═══════════════════════════════════════════════════════════════════
   Route Loading Fallback
   ═══════════════════════════════════════════════════════════════════ */

/**
 * Skeleton shown while lazy route chunks load.
 * Mimics a typical page layout: header + stats row + body area.
 */
function RouteLoadingFallback() {
  return (
    <div className="route-skeleton">
      <div className="route-skeleton__header" />
      <div className="route-skeleton__subtitle" />
      <div className="route-skeleton__stats">
        <div className="route-skeleton__stat" />
        <div className="route-skeleton__stat" />
        <div className="route-skeleton__stat" />
        <div className="route-skeleton__stat" />
      </div>
      <div className="route-skeleton__body" />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Lazy Page Imports
   ═══════════════════════════════════════════════════════════════════ */

const CommandCenter = lazy(() => import('@/pages/dashboard/CommandCenter'));
const BlockChartPage = lazy(() => import('@/pages/chart/BlockChartPage'));
const PositionsPage = lazy(() => import('@/pages/positions/PositionsPage'));
const NewPositionPage = lazy(() => import('@/pages/positions/NewPositionPage'));
const EditPositionPage = lazy(() => import('@/pages/positions/EditPositionPage'));
const MembersPage = lazy(() => import('@/pages/members/MembersPage'));
const NewMemberPage = lazy(() => import('@/pages/members/NewMemberPage'));
const EditMemberPage = lazy(() => import('@/pages/members/EditMemberPage'));
const ShiftSchedulePage = lazy(() => import('@/pages/shifts/ShiftSchedulePage'));
const ScheduleDetailPage = lazy(() => import('@/pages/schedule/ScheduleDetailPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const InfoHubPage = lazy(() => import('@/pages/info/InfoHubPage'));
const NotFoundPage = lazy(() => import('@/pages/error/NotFoundPage'));

/* ═══════════════════════════════════════════════════════════════════
   Route Definitions
   ═══════════════════════════════════════════════════════════════════ */

/**
 * Creates the application route tree.
 *
 * All page routes are nested under the AppShell layout route,
 * which provides the sidebar, topbar, and mobile navigation.
 * Each page is wrapped in a Suspense boundary for code splitting.
 */
function createAppRoutes(): RouteObject[] {
  return [
    /* ── Main app routes (inside AppShell layout) ──────────── */
    {
      element: <AppShell />,
      children: [
        {
          index: true,
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <CommandCenter />
            </Suspense>
          ),
        },

        /* ── Block Chart ───────────────────────────────────── */
        {
          path: 'chart',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <BlockChartPage />
            </Suspense>
          ),
        },

        /* ── Positions ──────────────────────────────────────── */
        {
          path: 'positions',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <PositionsPage />
            </Suspense>
          ),
        },
        {
          path: 'positions/new',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <NewPositionPage />
            </Suspense>
          ),
        },
        {
          path: 'positions/:id/edit',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <EditPositionPage />
            </Suspense>
          ),
        },

        /* ── Members ────────────────────────────────────────── */
        {
          path: 'members',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <MembersPage />
            </Suspense>
          ),
        },
        {
          path: 'members/new',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <NewMemberPage />
            </Suspense>
          ),
        },
        {
          path: 'members/:id/edit',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <EditMemberPage />
            </Suspense>
          ),
        },

        /* ── Schedule (kept for schedule detail view) ──────── */
        {
          path: 'shifts/:id/schedule',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <ShiftSchedulePage />
            </Suspense>
          ),
        },
        {
          path: 'schedule/:id',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <ScheduleDetailPage />
            </Suspense>
          ),
        },

        /* ── Settings ───────────────────────────────────────── */
        {
          path: 'settings',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <SettingsPage />
            </Suspense>
          ),
        },

        /* ── Info Hub ───────────────────────────────────────── */
        {
          path: 'info',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <InfoHubPage />
            </Suspense>
          ),
        },

        /* ── 404 Catch-All ──────────────────────────────────── */
        {
          path: '*',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <NotFoundPage />
            </Suspense>
          ),
        },
      ],
    },
  ];
}

export { createAppRoutes, RouteLoadingFallback };
